import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X as XIcon, Upload, ArrowUp, ArrowDown, Eye, EyeOff, ArrowLeft, Star, GripVertical, Image as ImageIcon, CheckSquare, Square } from "lucide-react";
import { uploadFilesInBatches } from "@/lib/uploadQueue";

/* ------------------------------- Gallery LIST ------------------------------- */
export function GalleriesAdmin() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ title: "", description: "", category: "Weddings", published: true });

  const load = async () => {
    try { const r = await api.get("/admin/galleries"); setRows(r.data); }
    catch (e) { if (e.response?.status === 401) { localStorage.removeItem("ep_token"); nav("/admin/login"); } }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    if (!draft.title.trim()) { toast.error("Title required"); return; }
    try {
      const r = await api.post("/admin/galleries", draft);
      toast.success("Gallery created");
      setCreating(false);
      setDraft({ title: "", description: "", category: "Weddings", published: true });
      nav(`/admin/galleries/${r.data.id}`);
    } catch { toast.error("Could not create"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this gallery and all its photos?")) return;
    await api.delete(`/admin/galleries/${id}`);
    toast.success("Deleted");
    load();
  };

  const togglePublish = async (g) => {
    await api.put(`/admin/galleries/${g.id}`, { published: !g.published });
    load();
  };

  return (
    <div data-testid="galleries-admin">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-3xl">Galleries</h2>
        <button onClick={() => setCreating(true)} className="btn-gold" data-testid="new-gallery-btn">
          <Plus size={14}/> New Gallery
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map(g => (
          <div key={g.id} className="bg-[#0a0a0a] border border-white/10 group" data-testid={`gallery-${g.slug}`}>
            <div className="aspect-[4/3] bg-black overflow-hidden relative">
              {g.cover_image ? (
                <img src={g.cover_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/20"><ImageIcon size={48}/></div>
              )}
              <div className="absolute top-3 right-3 flex gap-1">
                <button onClick={() => togglePublish(g)} title={g.published ? "Hide" : "Publish"}
                  className={`p-1.5 ${g.published ? "bg-[#D4AF37] text-black" : "bg-black/70 text-white/60"}`}>
                  {g.published ? <Eye size={14}/> : <EyeOff size={14}/>}
                </button>
              </div>
            </div>
            <div className="p-5">
              <h3 className="font-serif text-xl">{g.title}</h3>
              <div className="text-xs text-white/40 mt-1">/portfolio/{g.slug} · {g.photo_count} {g.photo_count === 1 ? "photo" : "photos"}</div>
              {g.description && <p className="text-sm text-white/55 mt-2 line-clamp-2">{g.description}</p>}
              <div className="flex gap-2 mt-4">
                <Link to={`/admin/galleries/${g.id}`} className="btn-gold text-[0.65rem] py-2 px-3 flex-1" data-testid={`edit-gallery-${g.slug}`}>Manage</Link>
                <button onClick={() => remove(g.id)} className="text-white/40 hover:text-red-500 p-2"><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-full text-center text-white/40 py-20">No galleries yet. Create one to get started.</div>}
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-6" onClick={() => setCreating(false)}>
          <div className="bg-[#0a0a0a] border border-white/15 max-w-lg w-full p-8" onClick={e=>e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-6">New Gallery</h3>
            <div className="space-y-4">
              <input placeholder="Title (e.g., Aisha & Daniyal)" value={draft.title} onChange={e=>setDraft({...draft, title:e.target.value})}
                data-testid="new-gallery-title"
                className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none" />
              <select value={draft.category} onChange={e=>setDraft({...draft, category:e.target.value})}
                className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none">
                {["Weddings","Engagements","Afghan Weddings","Cultural Weddings","Destination Weddings","Bridal Sessions","Outdoor Weddings","Wedding Films"].map(c => <option key={c}>{c}</option>)}
              </select>
              <textarea placeholder="Description (optional)" rows={3} value={draft.description} onChange={e=>setDraft({...draft, description:e.target.value})}
                className="w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none" />
              <div className="flex gap-3">
                <button onClick={save} className="btn-gold" data-testid="save-new-gallery">Create</button>
                <button onClick={() => setCreating(false)} className="btn-ghost">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Gallery EDITOR ------------------------------- */
export default function GalleryEditor() {
  const { gid } = useParams();
  const nav = useNavigate();
  const [g, setG] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState([]); // [{id, name, pct, status, error?}]
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const fileRef = useRef(null);

  const load = async () => {
    try { const r = await api.get(`/admin/galleries/${gid}`); setG(r.data); }
    catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("ep_token"); nav("/admin/login"); }
      else if (e.response?.status === 404) nav("/admin");
    }
  };
  useEffect(() => {
    if (!localStorage.getItem("ep_token")) nav("/admin/login");
    load();
    /* eslint-disable-next-line */
  }, [gid]);

  const saveMeta = async (patch) => {
    await api.put(`/admin/galleries/${gid}`, patch);
    toast.success("Saved");
    load();
  };

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setQueue(files.map((f, i) => ({ id: i, name: f.name, pct: 0, status: "pending" })));

    // Attach each uploaded media to the gallery as it completes
    const attached = new Set();
    const { ok, fail } = await uploadFilesInBatches({
      files,
      concurrency: 3,
      maxRetries: 2,
      onProgress: (id, pct) => setQueue(prev => prev.map(q =>
        q.id === id ? { ...q, pct, status: pct >= 100 ? "attaching" : "uploading" } : q)),
      onItemDone: async (id, data) => {
        try {
          await api.post(`/admin/galleries/${gid}/photos`, {
            url: data.url, storage_path: data.path,
            alt: files[id].name.replace(/\.[^.]+$/, ""),
          });
          attached.add(id);
          setQueue(prev => prev.map(q => q.id === id ? { ...q, status: "done", pct: 100 } : q));
          load();
        } catch (err) {
          setQueue(prev => prev.map(q => q.id === id ? { ...q, status: "error", error: "Saved upload but could not attach to gallery" } : q));
        }
      },
      onItemFail: (id, err) => setQueue(prev => prev.map(q =>
        q.id === id ? { ...q, status: "error", error: err?.userMessage || err?.response?.data?.detail || err?.message || "Upload failed" } : q)),
    });
    setUploading(false);
    if (ok && attached.size) toast.success(`Uploaded ${attached.size} photo${attached.size === 1 ? "" : "s"}`);
    if (fail) toast.error(`${fail} upload${fail === 1 ? "" : "s"} failed`);
    e.target.value = "";
    if (!fail) setTimeout(() => setQueue([]), 1800);
  };

  const deletePhoto = async (pid) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/admin/galleries/${gid}/photos/${pid}`);
    toast.success("Deleted");
    load();
  };

  const togglePhotoSel = (pid) => {
    setSelectedPhotos(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };
  const selectAllPhotos = () => setSelectedPhotos(new Set((g?.photos || []).map(p => p.id)));
  const deselectAllPhotos = () => setSelectedPhotos(new Set());
  const bulkDeletePhotos = async () => {
    const ids = Array.from(selectedPhotos);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} photo${ids.length === 1 ? "" : "s"} from this gallery?`)) return;
    try {
      const r = await api.post(`/admin/galleries/${gid}/photos/bulk-delete`, { ids });
      toast.success(`Deleted ${r.data.deleted}`);
      setSelectedPhotos(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
  };

  const setCover = async (url) => {
    await api.put(`/admin/galleries/${gid}`, { cover_image: url });
    toast.success("Cover updated");
    load();
  };

  const movePhoto = async (idx, dir) => {
    const photos = [...g.photos];
    const ni = idx + dir;
    if (ni < 0 || ni >= photos.length) return;
    [photos[idx], photos[ni]] = [photos[ni], photos[idx]];
    setG({ ...g, photos });
    await api.post(`/admin/galleries/${gid}/reorder`, { ordered_ids: photos.map(p => p.id) });
  };

  /** Drag-and-drop reordering — native HTML5, auto-saves to /reorder on drop */
  const dragFrom = useRef(null);
  const onPhotoDragStart = (i) => (e) => {
    dragFrom.current = i;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(i));
  };
  const onPhotoDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onPhotoDrop = (targetIdx) => async (e) => {
    e.preventDefault();
    const src = dragFrom.current ?? Number(e.dataTransfer.getData("text/plain"));
    dragFrom.current = null;
    if (isNaN(src) || src === targetIdx) return;
    const photos = [...g.photos];
    const [moved] = photos.splice(src, 1);
    photos.splice(targetIdx, 0, moved);
    setG({ ...g, photos });
    try {
      await api.post(`/admin/galleries/${gid}/reorder`, { ordered_ids: photos.map(p => p.id) });
    } catch { toast.error("Could not save new order"); load(); }
  };

  const updatePhotoMeta = async () => {
    if (!editingPhoto) return;
    await api.put(`/admin/galleries/${gid}/photos/${editingPhoto.id}`, {
      alt: editingPhoto.alt || "",
      caption: editingPhoto.caption || "",
      focal_x: editingPhoto.focal_x ?? 50,
      focal_y: editingPhoto.focal_y ?? 50,
    });
    toast.success("Saved");
    setEditingPhoto(null);
    load();
  };

  if (!g) return <div className="min-h-screen bg-[#070707] flex items-center justify-center text-white/40">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#070707] flex" data-testid="gallery-editor">
      <aside className="w-64 border-r border-white/10 p-6 sticky top-0 h-screen flex flex-col">
        <div className="flex items-center gap-3 mb-10">
          <img src={LOGO_URL} alt="" className="h-12 object-contain" />
          <div>
            <div className="font-serif text-base leading-tight">Elite Pixels</div>
            <div className="text-[0.55rem] tracking-[0.25em] uppercase text-white/40">Admin</div>
          </div>
        </div>
        <Link to="/admin" className="text-xs text-white/60 hover:text-[#D4AF37] flex items-center gap-2 mb-4" data-testid="back-to-admin">
          <ArrowLeft size={14}/> Back to Dashboard
        </Link>
      </aside>

      <main className="flex-1 p-8 overflow-x-auto">
        {/* Header & Meta editor */}
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="eyebrow">Gallery</div>
            <h2 className="font-serif text-4xl mt-2">{g.title}</h2>
            <div className="text-xs text-white/40 mt-1">/portfolio/{g.slug} · {(g.photos||[]).length} photos · {g.published ? "Published" : "Hidden"}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => saveMeta({ published: !g.published })} className="btn-ghost" data-testid="toggle-publish">
              {g.published ? <><EyeOff size={14}/> Hide</> : <><Eye size={14}/> Publish</>}
            </button>
            <Link to={`/portfolio/${g.slug}`} target="_blank" className="btn-ghost" data-testid="view-public">View Public</Link>
          </div>
        </div>

        {/* Meta editor */}
        <div className="grid lg:grid-cols-3 gap-4 mb-8">
          <input value={g.title} onChange={e => setG({...g, title:e.target.value})} onBlur={e => saveMeta({ title: e.target.value })}
            placeholder="Title" data-testid="meta-title"
            className="bg-black border border-white/15 px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none" />
          <select value={g.category} onChange={e => { setG({...g, category:e.target.value}); saveMeta({ category: e.target.value }); }}
            data-testid="meta-category"
            className="bg-black border border-white/15 px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none">
            {["Weddings","Engagements","Afghan Weddings","Cultural Weddings","Destination Weddings","Bridal Sessions","Outdoor Weddings","Wedding Films"].map(c => <option key={c}>{c}</option>)}
          </select>
          <input value={g.seo_title || ""} onChange={e => setG({...g, seo_title:e.target.value})} onBlur={e => saveMeta({ seo_title: e.target.value })}
            placeholder="SEO Title" data-testid="meta-seo-title"
            className="bg-black border border-white/15 px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none" />
          <textarea value={g.description || ""} onChange={e => setG({...g, description:e.target.value})} onBlur={e => saveMeta({ description: e.target.value })}
            placeholder="Description (used on /portfolio cards and the gallery page)" rows={2} data-testid="meta-description"
            className="lg:col-span-2 bg-black border border-white/15 px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none" />
          <textarea value={g.seo_description || ""} onChange={e => setG({...g, seo_description:e.target.value})} onBlur={e => saveMeta({ seo_description: e.target.value })}
            placeholder="SEO Meta Description (under 160 chars)" rows={2} data-testid="meta-seo-description"
            className="bg-black border border-white/15 px-4 py-2.5 text-sm focus:border-[#D4AF37] outline-none" />
        </div>

        {/* Uploader */}
        <div className="mb-8 p-8 border-2 border-dashed border-white/15 text-center bg-[#0a0a0a]" data-testid="uploader">
          <input ref={fileRef} type="file" multiple accept="image/*" onChange={onFilesPicked} className="hidden" data-testid="file-input" />
          <Upload size={28} className="mx-auto text-[#D4AF37] mb-4" />
          <h3 className="font-serif text-xl mb-2">{uploading ? "Uploading..." : "Add photos to this gallery"}</h3>
          <p className="text-xs text-white/40 mb-5">JPG, PNG, WebP — up to 50 MB each. Select multiple at once.</p>
          <button disabled={uploading} onClick={() => fileRef.current?.click()} className="btn-gold" data-testid="upload-btn">
            <Upload size={14}/> {uploading ? "Uploading..." : "Choose Files"}
          </button>
        </div>

        {/* Per-file upload queue */}
        {queue.length > 0 && (
          <div className="mb-8 bg-[#0a0a0a] border border-white/10 p-4 space-y-2 max-h-64 overflow-y-auto" data-testid="upload-queue">
            {queue.map(q => (
              <div key={q.id} className="text-xs">
                <div className="flex justify-between gap-3 mb-1">
                  <span className="truncate text-white/80" title={q.name}>{q.name}</span>
                  <span className={`shrink-0 ${q.status === "error" ? "text-red-400" : q.status === "done" ? "text-[#D4AF37]" : "text-white/40"}`}>
                    {q.status === "error" ? "Failed" : q.status === "done" ? "Done" : q.status === "attaching" ? "Saving…" : `${q.pct}%`}
                  </span>
                </div>
                <div className="h-1 bg-white/10 overflow-hidden rounded-sm">
                  <div className={`h-full transition-all ${q.status === "error" ? "bg-red-500" : "bg-[#D4AF37]"}`}
                       style={{ width: `${q.status === "error" ? 100 : q.pct}%` }} />
                </div>
                {q.status === "error" && q.error && (
                  <div className="text-[0.65rem] text-red-400/80 mt-1">{q.error}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Photo grid */}
        {(g.photos || []).length === 0 ? (
          <div className="text-center text-white/30 py-20">No photos yet — upload your first ones above.</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4 text-xs" data-testid="photos-bulk-bar">
              <button onClick={selectAllPhotos} className="btn-ghost" data-testid="select-all-photos">
                <CheckSquare size={12}/> Select All ({g.photos.length})
              </button>
              {selectedPhotos.size > 0 && (
                <>
                  <button onClick={deselectAllPhotos} className="btn-ghost" data-testid="deselect-all-photos">
                    <Square size={12}/> Deselect ({selectedPhotos.size})
                  </button>
                  <button onClick={bulkDeletePhotos} className="btn-ghost text-red-300 hover:text-red-500 border-red-500/40" data-testid="bulk-delete-photos">
                    <Trash2 size={12}/> Delete {selectedPhotos.size} selected
                  </button>
                </>
              )}
              <span className="ml-auto text-white/40 italic">Drag any photo to reorder — saves automatically.</span>
            </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {g.photos.map((p, idx) => {
              const isSel = selectedPhotos.has(p.id);
              return (
              <div key={p.id}
                draggable
                onDragStart={onPhotoDragStart(idx)}
                onDragOver={onPhotoDragOver}
                onDrop={onPhotoDrop(idx)}
                className={`bg-[#0a0a0a] border ${isSel ? "border-[#D4AF37] ring-1 ring-[#D4AF37]" : "border-white/10"} group relative cursor-move`}
                data-testid={`photo-${p.id}`}>
                <button onClick={() => togglePhotoSel(p.id)} title={isSel ? "Deselect" : "Select"}
                  className={`absolute top-2 left-2 z-10 p-1.5 transition ${isSel ? "bg-[#D4AF37] text-black" : "bg-black/70 text-white/70 hover:bg-[#D4AF37] hover:text-black opacity-0 group-hover:opacity-100"}`}
                  data-testid={`select-photo-${p.id}`}>
                  {isSel ? <CheckSquare size={12}/> : <Square size={12}/>}
                </button>
                <div className="aspect-square overflow-hidden relative bg-black">
                  <img src={p.url} alt={p.alt || ""} className="w-full h-full object-cover"
                       style={{ objectPosition: `${p.focal_x ?? 50}% ${p.focal_y ?? 50}%` }} />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-2">
                    <div className="flex gap-1">
                      <button onClick={() => movePhoto(idx, -1)} className="p-2 bg-black/60 hover:bg-[#D4AF37] hover:text-black transition" disabled={idx===0} title="Move up"><ArrowUp size={14}/></button>
                      <button onClick={() => movePhoto(idx, 1)} className="p-2 bg-black/60 hover:bg-[#D4AF37] hover:text-black transition" disabled={idx===g.photos.length-1} title="Move down"><ArrowDown size={14}/></button>
                    </div>
                    <button onClick={() => setEditingPhoto({ ...p })} className="px-3 py-1.5 bg-black/60 hover:bg-[#D4AF37] hover:text-black text-xs tracking-widest uppercase" data-testid={`edit-photo-${p.id}`}><Edit3 size={12} className="inline mr-1"/>Edit Info</button>
                    <button onClick={() => setCover(p.url)} className="px-3 py-1.5 bg-black/60 hover:bg-[#D4AF37] hover:text-black text-xs tracking-widest uppercase" title="Set as cover"><Star size={12} className="inline mr-1"/>{g.cover_image === p.url ? "Cover" : "Set Cover"}</button>
                    <button onClick={() => deletePhoto(p.id)} className="px-3 py-1.5 bg-black/60 hover:bg-red-500 text-xs tracking-widest uppercase" data-testid={`delete-photo-${p.id}`}><Trash2 size={12} className="inline mr-1"/>Delete</button>
                  </div>
                  {g.cover_image === p.url && (
                    <div className="absolute top-2 right-2 bg-[#D4AF37] text-black text-[0.55rem] tracking-widest uppercase px-2 py-1">Cover</div>
                  )}
                </div>
                <div className="p-3 text-xs text-white/50 truncate">{p.alt || p.caption || "—"}</div>
              </div>
              );
            })}
          </div>
          </>
        )}
      </main>

      {/* Edit photo modal */}
      {editingPhoto && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={() => setEditingPhoto(null)} data-testid="edit-photo-modal">
          <div className="bg-[#0a0a0a] border border-white/15 max-w-2xl w-full p-8 my-8" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-2xl">Edit Photo</h3>
              <button onClick={() => setEditingPhoto(null)}><XIcon size={20}/></button>
            </div>

            {/* Focal point picker — click anywhere on the photo to choose what stays visible in the thumbnail */}
            <div className="mb-4">
              <div className="flex justify-between items-baseline mb-2">
                <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Thumbnail Focus Point</span>
                <span className="text-[0.6rem] text-white/40">click on the photo to pick</span>
              </div>
              <div
                className="relative w-full aspect-video overflow-hidden cursor-crosshair bg-black select-none"
                data-testid="focal-picker"
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  const x = Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
                  const y = Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100));
                  setEditingPhoto({ ...editingPhoto, focal_x: x, focal_y: y });
                }}
              >
                <img src={editingPhoto.url} alt="" className="absolute inset-0 w-full h-full object-contain pointer-events-none" />
                <div
                  className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 border-2 border-[#D4AF37] rounded-full shadow-[0_0_0_2px_rgba(0,0,0,0.6)] pointer-events-none"
                  style={{ left: `${editingPhoto.focal_x ?? 50}%`, top: `${editingPhoto.focal_y ?? 50}%` }}
                  data-testid="focal-marker"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                <label className="text-white/60">
                  X: <input type="number" min="0" max="100" step="1"
                    value={Math.round(editingPhoto.focal_x ?? 50)}
                    onChange={(e) => setEditingPhoto({ ...editingPhoto, focal_x: Number(e.target.value) })}
                    className="ml-2 w-16 bg-black border border-white/15 px-2 py-1 focus:border-[#D4AF37] outline-none"
                    data-testid="focal-x-input" /> %
                </label>
                <label className="text-white/60">
                  Y: <input type="number" min="0" max="100" step="1"
                    value={Math.round(editingPhoto.focal_y ?? 50)}
                    onChange={(e) => setEditingPhoto({ ...editingPhoto, focal_y: Number(e.target.value) })}
                    className="ml-2 w-16 bg-black border border-white/15 px-2 py-1 focus:border-[#D4AF37] outline-none"
                    data-testid="focal-y-input" /> %
                </label>
              </div>

              {/* Live square-thumbnail preview at the chosen focus */}
              <div className="mt-4">
                <div className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60 mb-2">Thumbnail preview</div>
                <div className="w-32 h-32 overflow-hidden bg-black border border-white/10" data-testid="focal-preview">
                  <img src={editingPhoto.url} alt=""
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `${editingPhoto.focal_x ?? 50}% ${editingPhoto.focal_y ?? 50}%` }} />
                </div>
              </div>
            </div>

            <label className="block mb-4">
              <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Alt Text (SEO)</span>
              <input value={editingPhoto.alt || ""} onChange={e=>setEditingPhoto({...editingPhoto, alt:e.target.value})}
                data-testid="photo-alt"
                placeholder="Bride in white dress at Casa Loma"
                className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            </label>
            <label className="block mb-6">
              <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Caption (shown on hover)</span>
              <input value={editingPhoto.caption || ""} onChange={e=>setEditingPhoto({...editingPhoto, caption:e.target.value})}
                data-testid="photo-caption"
                placeholder="Aisha & Daniyal — Casa Loma"
                className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
            </label>
            <div className="flex gap-3">
              <button onClick={updatePhotoMeta} className="btn-gold" data-testid="save-photo-meta">Save</button>
              <button onClick={() => setEditingPhoto(null)} className="btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
